'use client'

import _ from 'lodash'
import { useEffect } from 'react'
import { redirect } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { useMutation } from "@tanstack/react-query"
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCookieState } from 'ahooks'

import { Alert, Button, Text, Password } from '@/components'
import { useGlobalStore } from '@/store/provider'
import { authApi } from '@/api'
import { Auth } from '@/types'
import { pages } from '@/settings'

const schema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
  password: z.string().min(1, 'Password is required'),
})

export function SignInWithEmail({ afterSignIn }: Auth.SignInProps) {
  const [myLoc, setMyLoc] = useCookieState('vehicle_loc')
  const { user, setUser } = useGlobalStore((state) => state)

  // react-hook-form
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(schema)
  })

  // submit
  const { mutate, error, isPending } = useMutation({ mutationFn: signIn })
  const onSubmit = (data: any) => { mutate(data) }

  // signIn function
  async function signIn(data: Auth.SignInRequest) {
    try {
      const d = await authApi.signIn(data)
      if (d?.data) {
        setUser(d.data, null)
        // set default zip code & radius
        if (!myLoc && !_.isEmpty(d?.data?.location)) {
          setMyLoc(JSON.stringify({
            zip_code: d.data?.location?.zip_code,
            radius: 200
          }))
        }
      }
    } catch (err) {
      return Promise.reject(err)
    }
  }

  useEffect(() => {
    if (_.isEmpty(user)) return
    if (afterSignIn) {
      afterSignIn()
    } else {
      redirect(pages.myProfile.href)
    }
  }, [user?._id])


  return (
    <form noValidate autoComplete='off' className='space-y-3' onSubmit={handleSubmit(onSubmit)}>
      <Text label='Email Address' type='email' {...register('email')} data-error={_.get(errors, 'email.message')} />
      <Password {...register('password')} data-error={_.get(errors, 'password.message')} />
      <div className='text-center'>
        {
          error?.message &&
          <Alert variant='error' message={error.message} />
        }
        <Button type='submit' className='w-full my-4' variant='primary' working={isPending}>
          Sign In
        </Button>
      </div>
    </form>

  )
}